import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, ArrowRight, TrendingUp } from "lucide-react";
import { EnhancedHeader } from "@/components/EnhancedHeader";
import { SEOHead } from "@/components/SEOHead";
import { StructuredData } from "@/components/StructuredData";

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  published_at: string;
  category: string;
  image_url?: string;
  reading_time: number;
}

export default function Blog() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      // Placeholder - will create blog_posts table later
      setPosts([
        {
          id: "1",
          title: "How AI is Revolutionizing Sports Betting in 2025",
          excerpt: "Discover how machine learning algorithms are transforming the betting landscape and helping tipsters make data-driven predictions.",
          content: "",
          author: "BetVerse AI Team",
          published_at: new Date().toISOString(),
          category: "AI & Technology",
          reading_time: 5
        },
        {
          id: "2",
          title: "Premier League Mid-Season Analysis: Top Value Bets",
          excerpt: "Our expert tipsters break down the EPL standings and reveal where the smart money is going for the second half of the season.",
          content: "",
          author: "Vincent Mwangangi",
          published_at: new Date(Date.now() - 86400000).toISOString(),
          category: "Match Analysis",
          reading_time: 7
        },
        {
          id: "3",
          title: "Understanding ROI: A Beginner's Guide to Betting Metrics",
          excerpt: "Learn how to track your betting performance, calculate ROI, and make smarter decisions with data-driven insights.",
          content: "",
          author: "BetVerse Education",
          published_at: new Date(Date.now() - 172800000).toISOString(),
          category: "Education",
          reading_time: 6
        }
      ]);
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "AI & Technology": return "bg-purple-500/10 text-purple-600 border-purple-500/20";
      case "Match Analysis": return "bg-blue-500/10 text-blue-600 border-blue-500/20";
      case "Education": return "bg-green-500/10 text-green-600 border-green-500/20";
      default: return "bg-muted text-muted-foreground";
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="BetVerse Blog - Betting Insights & AI Analysis | Expert Tips"
        description="Expert betting analysis, AI predictions, and sports insights from BetVerse. Stay ahead with match previews, tipster strategies, and market trends."
        canonical="https://betverse-global-picks.lovable.app/blog"
        type="website"
      />
      
      <StructuredData
        type="WebPage"
        data={{
          title: "BetVerse Blog - Betting Insights & AI Analysis",
          description: "Expert betting analysis, AI predictions, and sports insights from BetVerse.",
          url: "https://betverse-global-picks.lovable.app/blog"
        }}
      />
      
      <StructuredData
        type="BreadcrumbList"
        data={{
          items: [
            { name: "Home", url: "https://betverse-global-picks.lovable.app/" },
            { name: "Blog", url: "https://betverse-global-picks.lovable.app/blog" }
          ]
        }}
      />

      <EnhancedHeader />

      <main className="container mx-auto px-4 py-8 pt-24">
        <div className="max-w-6xl mx-auto">
          {/* Hero Section */}
          <div className="mb-12 text-center">
            <Badge className="mb-4" variant="outline">
              <TrendingUp className="w-3 h-3 mr-1" />
              Latest Insights
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              BetVerse Blog
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Expert betting analysis, AI predictions, and sports insights to help you stay ahead of the game
            </p>
          </div>

          {/* Blog Posts Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <Card key={i} className="animate-pulse">
                  <CardHeader>
                    <div className="h-6 bg-muted rounded w-3/4 mb-2" />
                    <div className="h-4 bg-muted rounded w-1/2" />
                  </CardHeader>
                  <CardContent>
                    <div className="h-20 bg-muted rounded" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map(post => (
                <Card key={post.id} className="hover:shadow-elegant transition-all duration-300 group">
                  <StructuredData
                    type="Article"
                    data={{
                      headline: post.title,
                      description: post.excerpt,
                      author: post.author,
                      datePublished: post.published_at,
                      url: `https://betverse-global-picks.lovable.app/blog/${post.id}`
                    }}
                  />
                  
                  <CardHeader>
                    <Badge className={`mb-2 w-fit ${getCategoryColor(post.category)}`}>
                      {post.category}
                    </Badge>
                    <CardTitle className="text-xl leading-tight group-hover:text-primary transition-colors">
                      {post.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {post.excerpt}
                    </p>

                    <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-border/50">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(post.published_at).toLocaleDateString()}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {post.reading_time} min read
                        </div>
                      </div>
                    </div>

                    <Button variant="ghost" size="sm" className="w-full group-hover:bg-primary/10">
                      Read More
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* CTA Section */}
          <Card className="mt-12 bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl font-bold mb-3">Want to Contribute?</h2>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                Are you a successful tipster with insights to share? Join our contributor program and help the BetVerse community grow.
              </p>
              <Button size="lg" variant="default">
                Become a Contributor
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
